import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowLeft, ChevronLeft, ChevronRight, RefreshCw, X, Map, Home, Building, User, FileText, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
// IMPORT THE NEW DETAIL MODAL
import TransactionDetailModal from './TransactionDetailModal';
import authFetch from '../../utils/authFetch';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const MasterSearchPage = () => {
  // --- STATE ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const LIMIT = 100;

  // Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCol, setSearchCol] = useState('all'); 
  const [activeSearch, setActiveSearch] = useState({ term: '', col: 'all' });

  // Popup State
  const [selectedItem, setSelectedItem] = useState(null);

  // --- API CALL ---
  const buildMasterPortalParams = ({ exportAll = false, pageValue = page, limitValue = LIMIT } = {}) => {
    const params = new URLSearchParams();

    if (!exportAll) {
      params.append('page', pageValue);
      params.append('limit', limitValue);
    } else {
      params.append('exportAll', 'true');
    }

    if (activeSearch.term) {
      params.append('search', activeSearch.term);
      params.append('searchCol', activeSearch.col);
    }

    return params;
  };

  const fetchServerData = () => {
    setLoading(true);

    const params = buildMasterPortalParams();
    
    authFetch(`${API_BASE}/api/master-portal?${params.toString()}`)
      .then(res => res.json())
      .then(response => {
        const processed = response.rows.map(parseRowData);
        setData(processed);
        setTotalRecords(response.total || 0);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  };

  // Trigger fetch on Page or Search change
  useEffect(() => {
    fetchServerData();
    window.scrollTo(0,0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeSearch]); 

  // --- HANDLERS ---
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setPage(1); // Reset to page 1
    setActiveSearch({ term: searchTerm, col: searchCol });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchCol('all');
    setActiveSearch({ term: '', col: 'all' });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(totalRecords / LIMIT)) {
      setPage(newPage);
    }
  };

  const handleExportToExcel = async () => {
    if (exporting || loading) return;

    try {
      setExporting(true);
      const params = buildMasterPortalParams({ exportAll: true });
      const response = await authFetch(`${API_BASE}/api/master-portal?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to export records: ${response.status}`);
      }

      const payload = await response.json();
      const rows = Array.isArray(payload.rows) ? payload.rows.map(parseRowData) : [];

      if (rows.length === 0) {
        window.alert('No records found for the current query.');
        return;
      }

      const exportRows = rows.map((row) => ({
        'Type': row.displayType,
        'KO': row.ko,
        'Parcela / Stavba': row.number,
        'Del': row.part,
        'ID Znak': row.id_sign || '',
        'Location': row.location || '',
        'Owners': row.owners && row.owners.length > 0
          ? row.owners.map((owner) => {
              const name = owner.imetnik_ime || owner.name || 'Unknown';
              return owner.delez ? `${name} (${owner.delez})` : name;
            }).join('; ')
          : '',
        'Date': row.date_formatted || '',
        'Source': row.source === 'ZK' ? 'ZK PDF' : row.source === 'ETN_RENTAL' ? 'RENTAL' : 'SALE',
        'Property Type': row.property_type || '',
        'Deal Type': row.deal_type || ''
      }));

      const sheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Master Search');

      const today = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `master_search_export_${today}.xlsx`);
    } catch (err) {
      console.error(err);
      window.alert('Unable to export the current query to Excel.');
    } finally {
      setExporting(false);
    }
  };

  // --- PARSER: EXTRACT ID PARTS ---
  const splitIdSignZK = (fullSign) => {
    if (!fullSign) return { ko: '-', number: '-', part: '-' };
    let sign = fullSign.toString().toLowerCase().trim();

    // 1. Remove Prefixes (Robustly)
    sign = sign.replace(/^(del stavbe|stavba|parcela|zemljiška parcela)\s+/i, '').trim();

    // 2. Split by Delimiters (Dash or Space)
    const parts = sign.split(/[\s-]+/).filter(p => p !== '');

    // 3. Assign based on Count
    if (parts.length >= 3) {
        return { ko: parts[0], number: parts[1], part: parts[2] };
    }
    if (parts.length === 2) {
        return { ko: parts[0], number: parts[1], part: '-' };
    }
    if (parts.length === 1 && parts[0].length > 0) {
        return { ko: parts[0], number: '-', part: '-' };
    }

    return { ko: '-', number: '-', part: '-' };
  };

  // --- MAIN ROW PARSER ---
  const parseRowData = (row) => {
    let displayType = 'UNKNOWN';
    const pType = (row.property_type || '').toLowerCase();
    const sign = (row.id_sign || '').toLowerCase(); 

    // 1. Determine Badge Type
    if (pType.includes('parcela') || sign.includes('parcela')) {
        displayType = 'PARCELA';
    }
    else if (pType.includes('del') || pType.includes('stavb') || sign.includes('del') || sign.includes('stavb')) {
        if (pType === 'stavba' || sign.startsWith('stavba')) {
            displayType = 'BUILDING';
        } else {
            displayType = 'DEL STAVBE';
        }
    }
    else if (row.source === 'ZK') {
        displayType = 'ZK ENTRY';
    }

    // 2. Parse ID Sign
    let parsedInfo = { ko: '-', number: '-', part: '-' };
    if (row.id_sign && row.id_sign.trim() !== '') {
        parsedInfo = splitIdSignZK(row.id_sign);
    } 

    // 3. Owners Parsing
    let parsedOwners = [];
    if (Array.isArray(row.owners_json)) {
        parsedOwners = row.owners_json;
    }

    return { 
        ...row, 
        displayType, 
        ko: parsedInfo.ko, 
        number: parsedInfo.number, 
        part: parsedInfo.part, 
        location: row.location || '-',
        owners: parsedOwners
    };
  };

  const totalPages = Math.ceil(totalRecords / LIMIT);

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', paddingBottom: '50px', fontFamily: 'Inter, sans-serif' }}>
      
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <Link to="/real-estate" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#64748b', textDecoration: 'none', fontSize: '14px' }}>
          <ArrowLeft size={16} /> Back to Hub
        </Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginTop: '10px' }}>
            <h1 style={{ color: '#1e293b', margin: 0 }}>Master Search Portal</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: '#64748b', fontSize: '13px' }}>
                  Found {totalRecords.toLocaleString()} records
              </span>
              <button
                type="button"
                onClick={handleExportToExcel}
                disabled={loading || exporting || totalRecords === 0}
                style={{
                  ...exportBtnStyle,
                  opacity: loading || exporting || totalRecords === 0 ? 0.6 : 1,
                  cursor: loading || exporting || totalRecords === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                <Download size={14} />
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </button>
            </div>
        </div>
      </div>

      {/* --- SEARCH BAR --- */}
      <div style={searchContainerStyle}>
        <form onSubmit={handleSearchSubmit} style={{display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap'}}>
          
          {/* Column Selector */}
          <div style={{display:'flex', flexDirection:'column', flex: '1 1 150px'}}>
            <label style={labelStyle}>Search In:</label>
            <select 
              value={searchCol} 
              onChange={(e) => setSearchCol(e.target.value)}
              style={{...selectStyle, width: '100%'}}
            >
              <option value="all">Vse (All Columns)</option>
              <option value="ko">Katastrska Občina (KO)</option>
              <option value="id">ID Znak (Parcel/Build)</option>
              <option value="owner">Lastnik (Owner)</option>
              <option value="date">Datum (Date)</option>
            </select>
          </div>

          {/* Text Input */}
          <div style={{display:'flex', flexDirection:'column', flexGrow: 1, minWidth: '200px'}}>
            <label style={labelStyle}>Query:</label>
            <div style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
              <Search size={16} color="#94a3b8" style={{position: 'absolute', left: '10px'}} />
              <input 
                type="text" 
                placeholder={searchCol === 'date' ? "e.g. 28.10.2025" : "Type to search..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={inputStyle}
              />
              {searchTerm && (
                <button type="button" onClick={handleClearSearch} style={clearButtonStyle}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div style={{display:'flex', flexDirection:'column', justifyContent:'flex-end', flex: '0 0 auto', marginTop: 'auto'}}>
             <button type="submit" style={submitBtnStyle}>Search</button>
          </div>

        </form>
      </div>

      {/* --- TABLE --- */}
      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <tr>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>KO</th>
              <th style={thStyle}>Parcela / Stavba</th>
              <th style={thStyle}>Del</th>
              <th style={thStyle}>Location (Muni/Street)</th>
              <th style={thStyle}>Owners</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Source</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
                <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center' }}>Loading...</td></tr>
            ) : data.length === 0 ? (
                <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No results found.</td></tr>
            ) : (
              data.map((row, i) => {
                const isParcela = row.displayType === 'PARCELA';
                const isBuilding = row.displayType === 'BUILDING';
                const isZK = row.displayType === 'ZK ENTRY';

                let badgeColor = '#eff6ff'; // Default Blue (Del Stavbe)
                let textColor = '#1d4ed8';
                let Icon = Home;

                if (isParcela) { badgeColor = '#ecfdf5'; textColor = '#047857'; Icon = Map; } 
                else if (isBuilding) { badgeColor = '#f3e8ff'; textColor = '#7e22ce'; Icon = Building; }
                else if (isZK) { badgeColor = '#fff7ed'; textColor = '#c2410c'; Icon = FileText; }

                return (
                  <tr 
                    key={i} 
                    onClick={() => setSelectedItem(row)} // Open Modal
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* 1. Type */}
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: badgeColor, color: textColor, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Icon size={12} /> {row.displayType}
                      </span>
                    </td>

                    {/* 2. KO */}
                    <td style={{...tdStyle, fontWeight: '600', color: '#334155'}}>{row.ko}</td>

                    {/* 3. Number */}
                    <td style={{...tdStyle, fontWeight: '700', color: '#1e293b'}}>{row.number}</td>

                    {/* 4. Part */}
                    <td style={{...tdStyle, color: '#64748b'}}>{row.part}</td>

                    {/* 5. Location */}
                    <td style={{...tdStyle, color: '#64748b', maxWidth: '200px'}}>{row.location}</td>
                    
                    {/* 6. Owners (Only shown for ZK, empty for Sales/Rentals as per request) */}
                    <td style={tdStyle}>
                      {row.owners && row.owners.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {row.owners.slice(0, 3).map((owner, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <User size={12} color="#94a3b8"/>
                                <span style={{ fontSize: '11px', color: '#334155', fontWeight: '500' }}>
                                    {owner.imetnik_ime || owner.name || 'Unknown'}
                                </span>
                                {owner.delez && (
                                    <span style={{ fontSize: '10px', color: '#64748b', background: '#f1f5f9', padding: '1px 5px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                        {owner.delez}
                                    </span>
                                )}
                            </div>
                          ))}
                          {row.owners.length > 3 && <span style={{fontSize:'10px', color:'#94a3b8'}}>+ {row.owners.length - 3} more</span>}
                        </div>
                      ) : <span style={{ color: '#cbd5e1' }}>-</span>}
                    </td>

                    {/* 7. Date (Fixed formatting) */}
                    <td style={{...tdStyle, whiteSpace:'nowrap', color:'#64748b'}}>
                        {row.date_formatted || '-'}
                    </td>
                    
                    {/* 8. Source */}
                    <td style={{...tdStyle, fontSize: '11px', color: '#94a3b8'}}>
                         <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                            {row.source === 'ZK' ? <FileText size={12} color="#b45309"/> : <RefreshCw size={12} />}
                            {row.source === 'ZK' ? 'ZK PDF' : row.source === 'ETN_RENTAL' ? 'RENTAL' : 'SALE'}
                         </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* --- PAGINATION --- */}
      <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
        <button 
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          style={{...paginationBtnStyle, opacity: page === 1 ? 0.5 : 1}}
        >
          <ChevronLeft size={16} /> Prev
        </button>
        <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>Page {page} of {totalPages || 1}</span>
        <button 
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          style={{...paginationBtnStyle, opacity: page === totalPages ? 0.5 : 1}}
        >
          Next <ChevronRight size={16} />
        </button>
      </div>

      {/* --- DETAIL POPUP --- */}
      {selectedItem && (
        <TransactionDetailModal 
          item={selectedItem} 
          onClose={() => setSelectedItem(null)} 
        />
      )}

    </div>
  );
};

// --- STYLES ---
const searchContainerStyle = { backgroundColor: '#fff', padding: '15px 20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' };
const labelStyle = { fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' };
const selectStyle = { padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: '#f8fafc', cursor: 'pointer', height: '38px' };
const inputStyle = { width: '100%', padding: '8px 12px 8px 35px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box', height: '38px' };
const clearButtonStyle = { position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' };
const submitBtnStyle = { height: '38px', padding: '0 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' };
const exportBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#0f766e', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600' };

const thStyle = { padding: '16px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '14px 16px', color: '#334155', verticalAlign: 'top' };
const badgeStyle = { padding: '4px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '11px', letterSpacing: '0.5px' };
const paginationBtnStyle = { display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 16px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', color: '#334155', fontWeight: '500' };

export default MasterSearchPage;